import { useState, useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { CheckCircle2, XCircle, ScanFace, Clock, Filter, MapPin, X, Download } from "lucide-react";
import { exportFaceLogPdf } from "@/lib/pdfExport";
import { format } from "date-fns";

interface VerificationLog {
  id: string;
  officer_id: string;
  officer_name: string;
  site_id: string | null;
  match: boolean;
  confidence: number;
  reason: string | null;
  liveness: boolean;
  verified_at: string;
  lat: number | null;
  lng: number | null;
}

function LogLocationMap({ lat, lng, officerName, onClose }: { lat: number; lng: number; officerName: string; onClose: () => void }) {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<any>(null);

  useEffect(() => {
    if (!mapRef.current) return;
    let cancelled = false;
    const loadMap = async () => {
      const L = await import("leaflet");
      if (!document.getElementById("leaflet-css-facelog")) {
        const link = document.createElement("link");
        link.id = "leaflet-css-facelog";
        link.rel = "stylesheet";
        link.href = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
        document.head.appendChild(link);
      }
      await new Promise(r => setTimeout(r, 150));
      if (cancelled || !mapRef.current) return;
      mapRef.current.innerHTML = "";
      const map = L.map(mapRef.current, { zoomControl: true, attributionControl: false }).setView([lat, lng], 16);
      L.tileLayer("https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png", {
        attribution: '&copy; CARTO',
        maxZoom: 19,
      }).addTo(map);
      L.marker([lat, lng]).addTo(map).bindPopup(`<b>${officerName}</b><br/>Verification location`).openPopup();
      L.circle([lat, lng], { radius: 50, color: "#D4AF37", fillOpacity: 0.15, weight: 1 }).addTo(map);
      mapInstanceRef.current = map;
      setTimeout(() => map.invalidateSize(), 200);
    };
    loadMap();
    return () => { cancelled = true; mapInstanceRef.current?.remove(); };
  }, [lat, lng, officerName]);

  return (
    <div className="relative mt-2 rounded-xl overflow-hidden border border-[hsl(var(--app-border-subtle))]">
      <button
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        className="absolute top-2 right-2 z-[1000] p-1 rounded-lg bg-[hsl(var(--app-bg)/0.9)] text-[hsl(var(--app-text-muted))] hover:text-[hsl(var(--app-text-primary))] transition-colors"
      >
        <X className="w-3.5 h-3.5" />
      </button>
      <div ref={mapRef} className="w-full h-[200px]" />
    </div>
  );
}

export default function FaceVerificationLogs({ sites }: { sites: { id: string; name: string }[] }) {
  const [filterStatus, setFilterStatus] = useState<"all" | "passed" | "failed">("all");
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [mapLogId, setMapLogId] = useState<string | null>(null);

  const { data: logs = [], isLoading } = useQuery({
    queryKey: ["face_verification_logs"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("face_verification_logs")
        .select("*")
        .order("verified_at", { ascending: false })
        .limit(200);
      if (error) throw error;
      return (data as any) as VerificationLog[];
    },
    refetchInterval: 5000,
  });

  const filtered = logs.filter((l) => {
    if (filterStatus === "passed") return l.match;
    if (filterStatus === "failed") return !l.match;
    return true;
  });

  const siteName = (id: string | null) => sites.find((s) => s.id === id)?.name || "—";

  const passCount = logs.filter((l) => l.match).length;
  const failCount = logs.filter((l) => !l.match).length;

  return (
    <div className="space-y-4">
      {/* Export button */}
      <div className="flex justify-end">
        <button
          onClick={() => exportFaceLogPdf(filtered)}
          className="flex items-center gap-1 px-3 py-1.5 rounded-lg border border-[hsl(var(--app-border-subtle))] text-[hsl(var(--app-text-secondary))] text-xs hover:border-[hsl(var(--app-gold))] hover:text-[hsl(var(--app-gold))] transition-colors"
        >
          <Download className="w-3.5 h-3.5" /> Export PDF
        </button>
      </div>
      {/* Summary cards */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-[hsl(var(--app-surface-2))] rounded-xl p-4 border border-[hsl(var(--app-gold)/0.15)]">
          <div className="flex items-center gap-2 text-xs text-[hsl(var(--app-text-secondary))] mb-1">
            <ScanFace className="w-3.5 h-3.5" /> Total Scans
          </div>
          <div className="text-2xl font-bebas text-[hsl(var(--app-text-primary))]">{logs.length}</div>
        </div>
        <div className="bg-[hsl(var(--app-surface-2))] rounded-xl p-4 border border-[hsl(var(--app-green)/0.2)]">
          <div className="flex items-center gap-2 text-xs text-[hsl(var(--app-green))] mb-1">
            <CheckCircle2 className="w-3.5 h-3.5" /> Passed
          </div>
          <div className="text-2xl font-bebas text-[hsl(var(--app-green))]">{passCount}</div>
        </div>
        <div className="bg-[hsl(var(--app-surface-2))] rounded-xl p-4 border border-[hsl(var(--app-red)/0.2)]">
          <div className="flex items-center gap-2 text-xs text-[hsl(var(--app-red))] mb-1">
            <XCircle className="w-3.5 h-3.5" /> Failed
          </div>
          <div className="text-2xl font-bebas text-[hsl(var(--app-red))]">{failCount}</div>
        </div>
      </div>

      {/* Filter */}
      <div className="flex items-center gap-2">
        <Filter className="w-4 h-4 text-[hsl(var(--app-text-secondary))]" />
        {(["all", "passed", "failed"] as const).map((s) => (
          <button
            key={s}
            onClick={() => setFilterStatus(s)}
            className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
              filterStatus === s
                ? "bg-[hsl(var(--app-gold))] text-[hsl(var(--app-bg))]"
                : "bg-[hsl(var(--app-surface-2))] text-[hsl(var(--app-text-secondary))] hover:text-[hsl(var(--app-text-primary))]"
            }`}
          >
            {s.charAt(0).toUpperCase() + s.slice(1)}
          </button>
        ))}
      </div>

      {/* Logs list */}
      {isLoading ? (
        <div className="text-center text-[hsl(var(--app-text-secondary))] py-8">Loading verification logs…</div>
      ) : filtered.length === 0 ? (
        <div className="text-center text-[hsl(var(--app-text-secondary))] py-8">No verification logs found</div>
      ) : (
        <div className="space-y-2 max-h-[500px] overflow-y-auto pr-1">
          {filtered.map((log) => (
            <div
              key={log.id}
              className={`rounded-xl border transition-colors cursor-pointer ${
                log.match
                  ? "bg-[hsl(var(--app-green)/0.05)] border-[hsl(var(--app-green)/0.2)]"
                  : "bg-[hsl(var(--app-red)/0.05)] border-[hsl(var(--app-red)/0.2)]"
              }`}
              onClick={() => setExpandedId(expandedId === log.id ? null : log.id)}
            >
              <div className="flex items-center gap-3 p-3">
                <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${
                  log.match ? "bg-[hsl(var(--app-green)/0.2)] text-[hsl(var(--app-green))]" : "bg-[hsl(var(--app-red)/0.2)] text-[hsl(var(--app-red))]"
                }`}>
                  {log.match ? <CheckCircle2 className="w-5 h-5" /> : <XCircle className="w-5 h-5" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-sm text-[hsl(var(--app-text-primary))] truncate">{log.officer_name}</span>
                    <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                      log.match ? "bg-[hsl(var(--app-green)/0.2)] text-[hsl(var(--app-green))]" : "bg-[hsl(var(--app-red)/0.2)] text-[hsl(var(--app-red))]"
                    }`}>
                      {log.confidence}%
                    </span>
                    {log.lat != null && log.lng != null && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[hsl(var(--app-gold)/0.1)] text-[hsl(var(--app-gold))] flex items-center gap-0.5">
                        <MapPin className="w-2.5 h-2.5" /> GPS
                      </span>
                    )}
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full flex items-center gap-0.5 ${
                      log.liveness ? "bg-blue-500/10 text-blue-400" : "bg-orange-500/10 text-orange-400"
                    }`}>
                      <ScanFace className="w-2.5 h-2.5" /> {log.liveness ? "Live" : "No Liveness"}
                    </span>
                  </div>
                  <div className="text-xs text-[hsl(var(--app-text-secondary))] mt-0.5 truncate">
                    {log.reason || (log.match ? "Identity verified" : "Not recognized")}
                  </div>
                  <div className="flex items-center gap-3 mt-1 text-[10px] text-[hsl(var(--app-text-secondary))]">
                    <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{format(new Date(log.verified_at), "dd MMM yyyy, HH:mm")}</span>
                    {log.site_id && <span>• {siteName(log.site_id)}</span>}
                  </div>
                </div>
              </div>

              {/* Expanded location details */}
              {expandedId === log.id && (
                <div className="px-3 pb-3 pt-1 border-t border-[hsl(var(--app-border-subtle))]">
                  {log.lat != null && log.lng != null ? (
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <p className="text-[10px] text-[hsl(var(--app-text-muted))] uppercase tracking-wider font-semibold mb-1">Verification Location</p>
                          <p className="text-xs font-mono text-[hsl(var(--app-text-secondary))]">
                            {log.lat.toFixed(6)}, {log.lng.toFixed(6)}
                          </p>
                        </div>
                        <button
                          onClick={(e) => { e.stopPropagation(); setMapLogId(mapLogId === log.id ? null : log.id); }}
                          className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[hsl(var(--app-gold)/0.1)] text-[hsl(var(--app-gold))] text-xs font-medium hover:bg-[hsl(var(--app-gold)/0.2)] transition-colors"
                        >
                          <MapPin className="w-3.5 h-3.5" /> {mapLogId === log.id ? "Hide Map" : "View on Map"}
                        </button>
                      </div>
                      {mapLogId === log.id && (
                        <LogLocationMap
                          lat={log.lat}
                          lng={log.lng}
                          officerName={log.officer_name}
                          onClose={() => setMapLogId(null)}
                        />
                      )}
                    </div>
                  ) : (
                    <p className="text-xs text-[hsl(var(--app-text-muted))] italic">Location not available — GPS was not enabled during verification</p>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}